import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { interiors } from '../assets/interiors';
import { initInteriorLightbox } from '../utils/initInteriorLightbox';
import useReveal from '../hooks/useReveal';
import { useSmoothScroll } from '../motion/SmoothScroll';

const filterCategories = [
  { id: 'all', label: 'All Spaces' },
  { id: 'living', label: 'Living & Lounges' },
  { id: 'suites', label: 'Master Suites' },
  { id: 'workspaces', label: 'Workspaces & Studios' },
  { id: 'foyers', label: 'Grand Foyers & Dining' },
];

const Interiors = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const galleryRef = useRef(null);
  const sectionRef = useRef(null);
  const { lenis } = useSmoothScroll() || {};

  const filteredInteriors = useMemo(() => {
    if (activeFilter === 'all') return interiors;
    return interiors.filter((item) => item.category === activeFilter);
  }, [activeFilter]);

  useReveal(sectionRef, '.interior-luxury-card', {
    y: 32,
    stagger: 0.04,
    start: 'top 90%',
  });

  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [lenis]);

  useEffect(
    () => initInteriorLightbox(galleryRef.current, 'a.interior-card-zoom'),
    [activeFilter]
  );

  return (
    <>
      {/* Editorial Interiors Header */}
      <section className="apple-banner interior-hero-banner">
        <div className="apple-wrap apple-center">
          <span className="apple-eyebrow">Interior Architecture & Joinery</span>
          <h1 className="interior-title-display">
            Spaces shaped with <span className="apple-italic">warmth & light</span>.
          </h1>
          <p className="interior-lede">
            Curated interior environments balancing tactile stone, custom architectural millwork, and layered ambient illumination.
          </p>

          {/* Interactive Zone Filter Tabs */}
          <div className="interior-filters" role="tablist">
            {filterCategories.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={`interior-filter-btn ${activeFilter === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Mosaic Gallery */}
      <section className="interiors-page-section" ref={sectionRef}>
        <div className="apple-wrap--wide">
          <div className="interior-gallery-stats-bar">
            <span className="interior-stats-count">
              <i className="fas fa-layer-group" aria-hidden /> Showing {filteredInteriors.length} Documented Spaces
            </span>
            <span className="interior-stats-hint">
              <i className="fas fa-search-plus" aria-hidden /> Click any view to enlarge in high resolution
            </span>
          </div>

          <div ref={galleryRef} className="interior-luxury-grid">
            <AnimatePresence>
              {filteredInteriors.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="interior-luxury-card"
                >
                  <a
                    href={item.image}
                    className="interior-card-zoom"
                    title={`${item.title} — ${item.subtitle}`}
                  >
                    <div className="interior-card-media">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                        className="interior-card-img"
                      />
                      <div className="interior-card-scrim" />

                      {/* Top Badges */}
                      <div className="interior-card-top-bar">
                        <span className="interior-num-tag">{item.number}</span>
                        <span className="interior-cat-tag">
                          <i className="fas fa-cube" aria-hidden /> {item.category}
                        </span>
                      </div>

                      {/* Floating Bottom Card Details */}
                      <div className="interior-card-glass-info">
                        <h3 className="interior-card-title">{item.title}</h3>
                        <p className="interior-card-subtitle">{item.subtitle}</p>

                        <div className="interior-card-action">
                          <span>View Detail Lightbox</span>
                          <div className="interior-arrow-circle">
                            <i className="fas fa-expand-alt" aria-hidden />
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Turnkey Interior Consultation Banner */}
          <div className="interior-bottom-cta">
            <div className="interior-cta-card">
              <div className="interior-cta-copy">
                <span className="cta-badge">Transform Your Space</span>
                <h3>Envisioning a bespoke interior transformation?</h3>
                <p>
                  From moodboards and 3D spatial visualizations to turnkey joinery installation, our studio executes complete luxury fitouts.
                </p>
              </div>
              <div className="interior-cta-actions">
                <a href="/#contact" className="interior-cta-btn">
                  <span>Schedule Interior Consultation</span>
                  <i className="fas fa-arrow-right" aria-hidden />
                </a>
                <Link to="/portfolio" className="interior-secondary-btn">
                  <span>View Full Architecture Works</span>
                  <i className="fas fa-arrow-right" aria-hidden />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Interiors;

